// views/Profile/components/UsageByAccountTable.jsx
import React from 'react';
import { TrendingUp, AlertCircle, ChevronRight } from 'lucide-react';
import DateRangePicker from './DateRangePicker';
import EmptyState from '../../../components/ui/EmptyState';
import './UsageByAccountTable.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * UsageByAccountTable — admin-only breakdown of pool usage by member
 * account.
 *
 * Sorts rows by totalCoins desc by default so the heaviest spender
 * floats to the top. Each row shows a horizontal share bar driven by
 * `row.totalCoins / totalCharged`. Clicking a row bubbles the
 * accountId + name to the parent so it can open the per-account
 * transaction drawer.
 *
 * Props:
 *   data:           { periodStart, periodEnd, totalCharged, byAccount[] }
 *   range:          { preset, since, until }
 *   onRangeChange:  (next) => void
 *   onRowClick:     (accountRow) => void
 *   loading, error
 */
export default function UsageByAccountTable({
  data,
  range,
  onRangeChange,
  onRowClick,
  loading = false,
  error = null,
}) {
  const totalCharged = data?.totalCharged ?? 0;
  const rows = (data?.byAccount ?? [])
    .slice()
    .sort((a, b) => (b.totalCoins ?? 0) - (a.totalCoins ?? 0));

  const periodStart = data?.periodStart;
  const periodEnd = data?.periodEnd;

  const showSkeleton = loading && !data;
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <section className="usage-by-account">
      <header className="usage-by-account__header">
        <div className="usage-by-account__title">
          <TrendingUp size={16} aria-hidden="true" />
          <h2>Usage by account</h2>
        </div>
        <DateRangePicker value={range} onChange={onRangeChange} />
      </header>

      {!loading && !error && (
        <p className="usage-by-account__total">
          Total charged this range:{' '}
          <strong>{formatCoins(totalCharged)}</strong> coins
        </p>
      )}

      {showSkeleton && (
        <div className="usage-by-account__skeleton" aria-busy="true">
          <div className="usage-by-account__skel-row" />
          <div className="usage-by-account__skel-row" />
          <div className="usage-by-account__skel-row" />
        </div>
      )}

      {error && (
        <div className="usage-by-account__error">
          <AlertCircle size={20} aria-hidden="true" />
          <p>{error.message || 'Unable to load usage by account'}</p>
          {error.requestId && (
            <small className="usage-by-account__request-id">
              ref: {error.requestId}
            </small>
          )}
        </div>
      )}

      {showEmpty && (
        <EmptyState
          variant="neutral"
          description="No accounts charged the pool in this range."
        />
      )}

      {!showSkeleton && !error && rows.length > 0 && (
        <div className="usage-by-account__scroll">
          <table className="usage-by-account__grid">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col" className="usage-by-account__th-right">
                  Coins
                </th>
                <th scope="col" className="usage-by-account__th-right">
                  Tx count
                </th>
                <th scope="col">Share</th>
                <th scope="col" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const share =
                  totalCharged > 0
                    ? (row.totalCoins / totalCharged) * 100
                    : 0;
                return (
                  <tr
                    key={row.accountId}
                    className="usage-by-account__row"
                    role="button"
                    tabIndex={0}
                    onClick={() => onRowClick?.(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick?.(row);
                      }
                    }}
                    aria-label={`Open transactions for ${
                      row.accountName ?? row.accountId
                    }`}
                  >
                    <td className="usage-by-account__name">
                      {row.accountName ?? row.accountId}
                    </td>
                    <td className="usage-by-account__coins">
                      {formatCoins(row.totalCoins)}
                    </td>
                    <td className="usage-by-account__count">
                      {formatCoins(row.transactionCount)}
                    </td>
                    <td className="usage-by-account__share">
                      <div className="usage-by-account__share-bar">
                        <div
                          className="usage-by-account__share-fill"
                          style={{ width: `${share.toFixed(1)}%` }}
                          aria-hidden="true"
                        />
                      </div>
                      <span className="usage-by-account__share-text">
                        {totalCharged > 0 ? `${share.toFixed(0)}%` : '—'}
                      </span>
                    </td>
                    <td className="usage-by-account__chevron">
                      <ChevronRight size={14} aria-hidden="true" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(periodStart || periodEnd) && !loading && !error && (
        <p className="usage-by-account__range">
          {formatDate(periodStart)} — {formatDate(periodEnd)}
        </p>
      )}
    </section>
  );
}
